/**
 * Informe diario — 3A attribution dashboard (impact kind)
 *
 * Renders 5 sub-tabs from pre-computed impact_serving.dashboard data.
 * Front computes NOTHING beyond en-US formatting, axis drawing, and
 * the 7-day trailing mean overlay. All figures are read directly from JSON.
 *
 * Data-science ref: b797bd58780f42fb17808170b8d19c9d65dac954
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  LabelList,
} from 'recharts';
import type {
  ImpactDashboardData,
  Channel,
  ChannelSeo,
  Attribution,
  AttributionOption,
  AttributionRow,
  AttributionTotal,
  MonthlyByChannelBands,
} from '@/services/impactDashboardService';

// ── en-US formatting ──────────────────────────────────────────────────────────

const esES = new Intl.NumberFormat('en-US');
const esESCur = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const esESPct = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
// Share values (share_whole_business, share_control): 2 decimals for ≥ 0.1 %; 3 decimals below
// per data-science contract dashboard-data.md @ b797bd5 line 76.
const esESPct2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const esESPct3 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

function fmtN(n: number | null | undefined): string {
  if (n == null) return '—';
  return esES.format(n);
}
function fmtEur(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${esESCur.format(n)} €`;
}
function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${esESPct.format(n)} %`;
}
export function fmtShare(n: number | null | undefined): string {
  if (n == null) return '—';
  const fmt = Math.abs(n) < 0.1 ? esESPct3 : esESPct2;
  return `${fmt.format(n)} %`;
}
function fmtPctSign(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${esESPct.format(n)} %`;
}
/** Short month label for chart axes: '2026-01' → 'Jan' */
function monthLabel(m: string): string {
  const date = new Date(`${m}-01T12:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short' });
}
/** Full capitalized month name: '2026-10' → 'October' */
function monthNameCap(m: string): string {
  const name = new Date(`${m}-01T12:00:00`).toLocaleDateString('en-US', { month: 'long' });
  return name.charAt(0).toUpperCase() + name.slice(1);
}
/** Number of calendar days in the month: '2026-10' → 31 */
function calendarDaysInMonth(yearMonth: string): number {
  const [y, mo] = yearMonth.split('-').map(Number);
  return new Date(y, mo, 0).getDate();
}
/** Short date for chart axes: '2026-01-15' → 'Jan 15' */
function dayLabel(d: string): string {
  const date = new Date(`${d}T12:00:00`);
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

// ── 7-day trailing mean (only allowed front computation) ──────────────────────

function trailing7dMean(values: number[]): (number | null)[] {
  return values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - 6), i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

// Attribution column key → English display name; unknown keys fall through to pipeline value
const ATTRIBUTION_COLUMN_NAMES: Record<string, string> = {
  revenue_medium_only: 'Channel origin',
  revenue_visit_any: 'Channel visit',
  revenue_medium_or_visit: 'Origin or visit',
  revenue_visit_same_product: 'Visited same product',
};

// Pipeline-supplied display labels — translate known Spanish values; unknown shown as-is and logged once
const ASSISTANT_LABELS: Record<string, string> = {
  'sin identificar': 'Unidentified',
};
const PROJECTION_GROUP_LABELS: Record<string, string> = {
  enlazable: 'Linkable',
  'sin cookie': 'No cookie',
  'relleno NOCID-': 'NOCID- filler',
};
const QUERY_TITLE_LABELS: Record<string, string> = {
  'La cohorte AEO': 'AEO cohort',
  'Visitas orgánicas': 'Organic visits',
  'Visitas desde asistentes de IA': 'AI assistant visits',
  'Compras': 'Purchases',
  'Visitas totales por tienda e idioma': 'Total visits by store and language',
  'Revenue total por tienda': 'Total revenue by store',
  'De dónde sale el % sin identificador': 'Where the unidentified % comes from',
};
const _warnedLabels = new Set<string>();
function pipelineLabel(map: Record<string, string>, value: string, ctx: string): string {
  if (!value) return value;
  const mapped = map[value];
  if (mapped !== undefined) return mapped;
  const key = `${ctx}:${value}`;
  if (!_warnedLabels.has(key)) {
    _warnedLabels.add(key);
    console.warn(`[InformeDiarioTab] unmapped ${ctx} label: "${value}" — add to map`);
  }
  return value;
}

// ── Option grouping helpers ───────────────────────────────────────────────────

const DIMENSION_LABELS: Record<string, string> = {
  domain: 'Store',
  language: 'Language',
  assistant: 'Assistant',
};

// KPI tile channel labels
const CH_VISIT_LABEL: Record<string, string> = {
  seo: 'Organic visits',
  llm: 'AI assistant visits',
};

const CH_SOURCE_LABEL: Record<string, string> = {
  seo: 'via organic search',
  llm: 'from an AI assistant',
};

function groupOptions(options: AttributionOption[]): Map<string, AttributionOption[]> {
  const groups = new Map<string, AttributionOption[]>();
  for (const opt of options) {
    const label = DIMENSION_LABELS[opt.dimension] ?? opt.dimension;
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(opt);
  }
  return groups;
}

// Sentinel used as Select value for "all / no filter" — Radix rejects empty string.
const ALL_OPT = '__all__';

// ── FilterSelect (visits or revenue) ─────────────────────────────────────────

export function FilterSelectVisits({
  options,
  value,
  onChange,
  totalLabel,
}: {
  options: AttributionOption[];
  value: string;
  onChange: (v: string) => void;
  totalLabel: string;
}) {
  const groups = groupOptions(options);
  return (
    <Select
      value={value === '' ? ALL_OPT : value}
      onValueChange={v => onChange(v === ALL_OPT ? '' : v)}
    >
      <SelectTrigger className="w-[240px] text-xs">
        <SelectValue placeholder="Entire channel" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_OPT}>Entire channel</SelectItem>
        {[...groups.entries()].map(([label, opts]) => (
          <SelectGroup key={label}>
            <SelectLabel>{label}</SelectLabel>
            {opts.map(opt => (
              <SelectItem key={opt.key} value={opt.key}>
                {(ASSISTANT_LABELS[opt.value] ?? opt.value)} · {fmtN(opt.total)} {totalLabel}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── DailyChart ────────────────────────────────────────────────────────────────

export function DailyChart({
  title,
  dates,
  allSeries,
  filteredSeries,
  filterMissing,
  dataKey,
  color,
  formatTooltip,
}: {
  title: string;
  dates: string[];
  allSeries: number[];
  filteredSeries: number[] | null;
  filterMissing: boolean;
  dataKey: string;
  color: string;
  formatTooltip: (v: number) => string;
}) {
  if (filterMissing) {
    return (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground py-8 text-center">
          Option not in data source (combination unavailable)
        </CardContent>
      </Card>
    );
  }
  const series = filteredSeries ?? allSeries;
  const mean = trailing7dMean(series);
  const chartData = dates.map((d, i) => ({
    date: d,
    [dataKey]: series[i] ?? null,
    media7d: mean[i],
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={60} tickFormatter={v => fmtN(v)} />
            <Tooltip
              {...IMPACT_TOOLTIP_PROPS}
              formatter={(v: number, name: string) => [
                formatTooltip(v),
                name === 'media7d' ? '7d avg' : title,
              ]}
              labelFormatter={dayLabel}
            />
            <Bar dataKey={dataKey} fill={color} opacity={0.7} radius={[1, 1, 0, 0]} />
            <Line
              dataKey="media7d"
              stroke="#374151"
              dot={false}
              strokeWidth={1.5}
              connectNulls={false}
              name="media7d"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ── Shared tooltip props (dark-mode fix) ─────────────────────────────────────
// Recharts' default box has a white background; in class-based dark mode the label
// inherits the light foreground and becomes invisible on white.  Forcing explicit
// dark text here keeps every Impact tooltip readable in both modes, matching the
// hard-coded white box so nothing else needs to change.
export const IMPACT_TOOLTIP_PROPS = {
  contentStyle: { background: '#fff', color: '#111827' },
  labelStyle: { color: '#111827' },
} as const;

// ── MonthlyChart ──────────────────────────────────────────────────────────────

// Cohort bar fill by metric
const COHORT_COLOR: Record<string, string> = {
  visits_per_day: '#4C7DE0',
  revenue_per_day: '#159E8E',
};

// Bands that carry % values (share strips) — routed through fmtShare in tooltips
const PCT_BANDS = new Set(['share_control', 'share_whole_business']);

// Consistent margin so all stacked panels share the same x-axis column widths
const CHART_MARGIN = { top: 18, right: 12, bottom: 0, left: 12 };

// True min-max domain for a band; handles flat lines (min===max) by padding ±1
export function bandDomain(values: (number | null)[]): [number | string, number | string] {
  const nums = values.filter((v): v is number => v != null);
  if (!nums.length) return ['dataMin', 'dataMax'];
  const mn = Math.min(...nums);
  const mx = Math.max(...nums);
  if (mn === mx) return [mn - 1, mx + 1];
  return ['dataMin', 'dataMax'];
}

export function MonthlyChart({
  title,
  months,
  days,
  bandsAll,
  bandsByOpt,
  filterKey,
  metric,
  formatTooltip,
  cohortProducts = 0,
}: {
  title: string;
  months: string[];
  days: number[];
  bandsAll: MonthlyByChannelBands;
  bandsByOpt: Record<string, MonthlyByChannelBands>;
  filterKey: string;
  metric: 'visits_per_day' | 'revenue_per_day';
  formatTooltip: (v: number) => string;
  cohortProducts?: number;
}) {
  if (filterKey && !bandsByOpt[filterKey]) {
    return (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground py-8 text-center">
          Option not in data source (combination unavailable)
        </CardContent>
      </Card>
    );
  }
  const rawBands = filterKey && bandsByOpt[filterKey] ? bandsByOpt[filterKey] : bandsAll;

  const wb = rawBands?.whole_business ?? null;
  const ctrl = rawBands?.control ?? null;
  const cohort = rawBands?.cohort ?? null;
  const shareWB = rawBands?.share_whole_business ?? null;
  const shareCtrl = rawBands?.share_control ?? null;

  const cohortColor = COHORT_COLOR[metric] ?? '#4C7DE0';

  // Per-panel chart data — each panel has its own data array (single series)
  const wbData = wb ? months.map((m, i) => ({ month: m, value: wb[i] ?? null })) : null;
  const ctrlData = ctrl ? months.map((m, i) => ({ month: m, value: ctrl[i] ?? null })) : null;
  const cohortData = months.map((m, i) => ({ month: m, value: cohort ? (cohort[i] ?? null) : null }));
  const shareWBData = shareWB ? months.map((m, i) => ({ month: m, value: shareWB[i] ?? null })) : null;
  const shareCtrlData = shareCtrl ? months.map((m, i) => ({ month: m, value: shareCtrl[i] ?? null })) : null;

  // Shared formatter: routes pct bands through fmtShare; preserves the series-name branch
  // so the mock Tooltip (which calls formatter for all sample names) produces different
  // output for cohort vs share_control — the chart-units regression guard relies on this.
  const tooltipFmt = (v: number, name: string): [string, string] =>
    [PCT_BANDS.has(name) ? fmtShare(v) : formatTooltip(v), name];

  // Single grouped tooltip for the cohort strip: closes over all band arrays so hovering
  // any strip (via syncId) produces ONE box with all present bands, not one box per strip.
  // Production Recharts uses `content` over `formatter`; the test mock uses `formatter`
  // (it ignores `content`) — both props are set on the cohort Tooltip so each path works.
  const tooltipContent = ({ active, label }: { active?: boolean; label?: string }) => {
    if (!active || !label) return null;
    const idx = months.indexOf(label);
    if (idx < 0) return null;
    const allBands: Array<{ name: string; value: number | null }> = [
      ...(wb        ? [{ name: 'whole_business',       value: wb[idx]        ?? null }] : []),
      ...(ctrl      ? [{ name: 'control',              value: ctrl[idx]      ?? null }] : []),
      { name: 'cohort',                               value: cohort ? (cohort[idx] ?? null) : null },
      ...(shareWB   ? [{ name: 'share_whole_business', value: shareWB[idx]   ?? null }] : []),
      ...(shareCtrl ? [{ name: 'share_control',        value: shareCtrl[idx] ?? null }] : []),
    ];
    const bands = allBands.filter((b): b is { name: string; value: number } => b.value !== null);
    if (!bands.length) return null;
    return (
      <div
        data-testid="grouped-tooltip-box"
        className="rounded border bg-white shadow-sm p-2 text-xs space-y-1"
        style={{ color: '#111827' }}
      >
        <div className="font-medium">{monthLabel(label)}</div>
        {bands.map(b => (
          <div key={b.name} className="flex gap-3 justify-between whitespace-nowrap">
            <span style={{ color: '#6b7280' }}>{b.name}</span>
            <span>{PCT_BANDS.has(b.name) ? fmtShare(b.value) : formatTooltip(b.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Partial-month note: only the LAST month, only when observed days < calendar days
  const lastIdx = months.length - 1;
  const lastObservedDays = typeof days[lastIdx] === 'number' ? days[lastIdx] : null;
  const isPartialLast = lastObservedDays !== null && lastObservedDays < calendarDaysInMonth(months[lastIdx]);

  const syncId = `monthly-${title}`;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {/* Legend: one swatch per active band */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
          <span className="flex items-center gap-1 text-xs">
            <svg width="12" height="10" aria-hidden><rect width="12" height="10" fill={cohortColor} /></svg>
            Bars · {filterKey ? 'products' : `the ${fmtN(cohortProducts)} products`} with AEO content
          </span>
          {wb && (
            <span className="flex items-center gap-1 text-xs">
              <svg width="16" height="4" aria-hidden><line x1="0" y1="2" x2="16" y2="2" stroke="#E2761B" strokeWidth="2" /></svg>
              Reference 1 · the whole stores, all channels (own scale)
            </span>
          )}
          {ctrl && (
            <span className="flex items-center gap-1 text-xs">
              <svg width="16" height="4" aria-hidden><line x1="0" y1="2" x2="16" y2="2" stroke="#22211e" strokeWidth="2" /></svg>
              Reference 2 · products without AEO content, same stores, same channel (own scale)
            </span>
          )}
          {shareWB && (
            <span className="flex items-center gap-1 text-xs">
              <svg width="16" height="4" aria-hidden><line x1="0" y1="2" x2="16" y2="2" stroke="#E2761B" strokeWidth="1.6" strokeDasharray="5 3" /></svg>
              Share 1
            </span>
          )}
          {shareCtrl && (
            <span className="flex items-center gap-1 text-xs">
              <svg width="16" height="4" aria-hidden><line x1="0" y1="2" x2="16" y2="2" stroke="#22211e" strokeWidth="1.6" strokeDasharray="2 2" /></svg>
              Share 2
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-0">
        {/* Strip 1: Reference 1 — whole_business (72px, own min-max scale) */}
        {wbData && (
          <ResponsiveContainer width="100%" height={72}>
            <ComposedChart data={wbData} margin={CHART_MARGIN} syncId={syncId}>
              <YAxis hide domain={bandDomain(wb)} />
              {/* scale="band" matches the cohort bar panel so all strips share the same month positions */}
              <XAxis dataKey="month" hide scale="band" />
              <Tooltip content={() => null} />
              <Line dataKey="value" stroke="#E2761B" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} name="whole_business">
                <LabelList dataKey="value" position="top" formatter={(v: number) => formatTooltip(v)} style={{ fontSize: 9 }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {/* Strip 2: Reference 2 — control (72px, own min-max scale) */}
        {ctrlData && (
          <ResponsiveContainer width="100%" height={72}>
            <ComposedChart data={ctrlData} margin={CHART_MARGIN} syncId={syncId}>
              <YAxis hide domain={bandDomain(ctrl)} />
              <XAxis dataKey="month" hide scale="band" />
              <Tooltip content={() => null} />
              <Line dataKey="value" stroke="#22211e" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} name="control">
                <LabelList dataKey="value" position="top" formatter={(v: number) => formatTooltip(v)} style={{ fontSize: 9 }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {/* Strip 3: Cohort bars (190px, domain [0, dataMax]).
            This is the only strip with a visible tooltip: `content` drives the grouped single-box
            tooltip in production Recharts; `formatter` is also set so the test mock (which calls
            formatter, not content) still exercises the pct-vs-absolute formatting branch.
            filterNull={false}: Recharts 2.x defaults to filterNull=true, which drops null payload
            entries and hides the bounding box when the filtered payload is empty. Without this,
            a month with cohort=null would hide the whole box even when wb/ctrl/shares have data.
            tooltipContent already omits null bands itself. */}
        <ResponsiveContainer width="100%" height={190}>
          <ComposedChart data={cohortData} margin={CHART_MARGIN} syncId={syncId}>
            <YAxis hide domain={[0, 'dataMax']} />
            <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10 }} />
            <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={tooltipFmt} content={tooltipContent} filterNull={false} />
            <Bar dataKey="value" fill={cohortColor} name="cohort">
              <LabelList dataKey="value" position="top" formatter={(v: number) => formatTooltip(v)} style={{ fontSize: 9 }} />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        {/* Strip 4: Share 1 — share_whole_business (58px, own min-max scale) */}
        {shareWBData && (
          <ResponsiveContainer width="100%" height={58}>
            <ComposedChart data={shareWBData} margin={CHART_MARGIN} syncId={syncId}>
              <YAxis hide domain={bandDomain(shareWB)} />
              <XAxis dataKey="month" hide scale="band" />
              <Tooltip content={() => null} />
              <Line dataKey="value" stroke="#E2761B" strokeWidth={1.6} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls={false} name="share_whole_business">
                <LabelList dataKey="value" position="top" formatter={(v: number) => fmtShare(v)} style={{ fontSize: 9 }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {/* Strip 5: Share 2 — share_control (58px, own min-max scale) */}
        {shareCtrlData && (
          <ResponsiveContainer width="100%" height={58}>
            <ComposedChart data={shareCtrlData} margin={CHART_MARGIN} syncId={syncId}>
              <YAxis hide domain={bandDomain(shareCtrl)} />
              <XAxis dataKey="month" hide scale="band" />
              <Tooltip content={() => null} />
              <Line dataKey="value" stroke="#22211e" strokeWidth={1.6} strokeDasharray="2 2" dot={{ r: 3 }} connectNulls={false} name="share_control">
                <LabelList dataKey="value" position="top" formatter={(v: number) => fmtShare(v)} style={{ fontSize: 9 }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {isPartialLast && (
          <p className="text-xs text-muted-foreground mt-1 px-3">
            {monthNameCap(months[lastIdx])} has {lastObservedDays} days so far: indicative.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── AttributionTable ──────────────────────────────────────────────────────────

export function AttributionTable({
  title,
  rows,
  total,
  columns,
  options,
  filterKey,
  onFilterChange,
  byOption,
}: {
  title: string;
  rows: AttributionRow[];
  total: AttributionTotal;
  columns: Attribution['columns'];
  options: AttributionOption[];
  filterKey: string;
  onFilterChange: (v: string) => void;
  byOption: Record<string, { rows: AttributionRow[]; total: Pick<AttributionTotal, 'values' | 'pct_no_id' | 'projection'> }>;
}) {
  const missingFilter = Boolean(filterKey) && !(filterKey in byOption);
  const activeRows = filterKey && !missingFilter ? byOption[filterKey].rows : rows;
  const activeTotal = filterKey && !missingFilter ? byOption[filterKey].total : total;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <FilterSelectVisits
            options={options}
            value={filterKey}
            onChange={onFilterChange}
            totalLabel="€"
          />
        </div>
      </CardHeader>
      {missingFilter ? (
        <CardContent className="text-xs text-muted-foreground py-8 text-center">
          Option not in data source (combination unavailable)
        </CardContent>
      ) : (
      <CardContent className="overflow-x-auto">
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              {columns.map(c => (
                <TableHead key={c.key} className="text-right">{ATTRIBUTION_COLUMN_NAMES[c.key] ?? c.name}</TableHead>
              ))}
              <TableHead className="text-right">% unidentified</TableHead>
              <TableHead className="text-right">~ projection</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeRows.map(row => (
              <TableRow key={row.month}>
                <TableCell>{row.month}</TableCell>
                {columns.map(c => (
                  <TableCell key={c.key} className="text-right">{fmtEur(row.values[c.key])}</TableCell>
                ))}
                <TableCell className="text-right">{fmtPct(row.pct_no_id)}</TableCell>
                <TableCell className="text-right text-muted-foreground">~{fmtEur(row.projection)}</TableCell>
              </TableRow>
            ))}
            {/* Total row */}
            <TableRow className="font-semibold border-t-2">
              <TableCell>Total</TableCell>
              {columns.map(c => (
                <TableCell key={c.key} className="text-right">{fmtEur(activeTotal.values[c.key])}</TableCell>
              ))}
              <TableCell className="text-right">{fmtPct(activeTotal.pct_no_id)}</TableCell>
              <TableCell className="text-right text-muted-foreground">~{fmtEur(activeTotal.projection)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
      )}
    </Card>
  );
}

// ── KpiTileRow ────────────────────────────────────────────────────────────────

function KpiTileRow({
  ch,
  chKey,
  comparison,
}: {
  ch: Channel | ChannelSeo;
  chKey: 'llm' | 'seo';
  comparison: ImpactDashboardData['comparison'];
}) {
  const pctWithUser = comparison.totals[chKey].pct_visits_with_user;
  return (
    <div className="grid grid-cols-3 gap-4">
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {CH_VISIT_LABEL[chKey]}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{fmtN(ch.totals.visits)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {fmtN(pctWithUser)} % with user_pseudo_id
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Revenue traced to a visit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{fmtEur(ch.totals.revenue_visit_any)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {fmtN(ch.totals.conv_visit_any)} purchase lines whose buyer visited a product page{' '}
            {CH_SOURCE_LABEL[chKey]} in the previous 14 days · {ATTRIBUTION_COLUMN_NAMES['revenue_visit_any']} · revenue_visit_any
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Products with any visit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{fmtN(ch.totals.products_with_visits)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            of the {fmtN(ch.totals.cohort_products)} with content, over the whole period
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── ChannelTab (p_llm / p_seo) ────────────────────────────────────────────────
// exported for direct test rendering

export function ChannelTab({
  ch,
  comparison,
  chKey,
}: (
  | { ch: Channel; chKey: 'llm' }
  | { ch: ChannelSeo; chKey: 'seo' }
) & { comparison: ImpactDashboardData['comparison'] }) {
  const [visFilter, setVisFilter] = useState('');
  const [mVisFilter, setMVisFilter] = useState('');
  const [revFilter, setRevFilter] = useState('');
  const [mRevFilter, setMRevFilter] = useState('');
  const [attrFilter, setAttrFilter] = useState('');

  const visOptions = ch.daily_by.options.visits;
  const revOptions = ch.daily_by.options.revenue_visit_any;

  const pctWithUser = comparison.totals[chKey].pct_visits_with_user;

  return (
    <div className="space-y-4">
      {/* KPI tiles */}
      <KpiTileRow ch={ch} chKey={chKey} comparison={comparison} />

      {/* Visits daily */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <DailyChart
            title="Visits per day"
            dates={ch.daily.dates}
            allSeries={ch.daily.visits}
            filteredSeries={visFilter ? (ch.daily_by.visits[visFilter] ?? null) : null}
            filterMissing={visFilter !== '' && ch.daily_by.visits[visFilter] == null}
            dataKey="visits"
            color="#3b82f6"
            formatTooltip={fmtN}
          />
        </div>
        <div className="pt-14">
          <FilterSelectVisits
            options={visOptions}
            value={visFilter}
            onChange={setVisFilter}
            totalLabel="visits"
          />
        </div>
      </div>

      {/* Visits monthly */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <MonthlyChart
            title="Visits · daily average per month"
            months={ch.monthly.months}
            days={ch.monthly.days}
            bandsAll={ch.monthly.visits_per_day}
            bandsByOpt={ch.monthly_by.visits_per_day}
            filterKey={mVisFilter}
            metric="visits_per_day"
            formatTooltip={fmtN}
            cohortProducts={ch.totals.cohort_products}
          />
        </div>
        <div className="pt-14">
          <FilterSelectVisits
            options={visOptions}
            value={mVisFilter}
            onChange={setMVisFilter}
            totalLabel="visits"
          />
        </div>
      </div>

      {/* Revenue daily */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <DailyChart
            title="Traced revenue, day by day"
            dates={ch.daily.dates}
            allSeries={ch.daily.revenue_visit_any}
            filteredSeries={revFilter ? (ch.daily_by.revenue_visit_any[revFilter] ?? null) : null}
            filterMissing={revFilter !== '' && ch.daily_by.revenue_visit_any[revFilter] == null}
            dataKey="revenue"
            color="#10b981"
            formatTooltip={fmtEur}
          />
        </div>
        <div className="pt-14">
          <FilterSelectVisits
            options={revOptions}
            value={revFilter}
            onChange={setRevFilter}
            totalLabel="€"
          />
        </div>
      </div>

      {/* Revenue monthly */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <MonthlyChart
            title="Traced revenue · daily average per month"
            months={ch.monthly.months}
            days={ch.monthly.days}
            bandsAll={ch.monthly.revenue_per_day}
            bandsByOpt={ch.monthly_by.revenue_per_day}
            filterKey={mRevFilter}
            metric="revenue_per_day"
            formatTooltip={fmtEur}
            cohortProducts={ch.totals.cohort_products}
          />

        </div>
        <div className="pt-14">
          <FilterSelectVisits
            options={revOptions}
            value={mRevFilter}
            onChange={setMRevFilter}
            totalLabel="€"
          />
        </div>
      </div>

      {/* Attribution table */}
      <AttributionTable
        title="The four attributions, month by month"
        rows={ch.attribution.rows}
        total={ch.attribution.total}
        columns={ch.attribution.columns}
        options={ch.attribution.options}
        filterKey={attrFilter}
        onFilterChange={setAttrFilter}
        byOption={ch.attribution.by_option}
      />

      {/* Assistants table — LLM channel only; SEO channel has no assistant data */}
      {chKey === 'llm' && <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Distribution by AI assistant</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Assistant</TableHead>
                <TableHead className="text-right">Visits</TableHead>
                <TableHead className="text-right">% visits</TableHead>
                <TableHead className="text-right">Traced revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(ch.assistants ?? []).map(a => (
                <TableRow key={a.assistant}>
                  <TableCell>{(ASSISTANT_LABELS[a.assistant] ?? a.assistant) || '(unnamed)'}</TableCell>
                  <TableCell className="text-right">{fmtN(a.visits)}</TableCell>
                  <TableCell className="text-right">{fmtPct(a.pct_visits)}</TableCell>
                  <TableCell className="text-right">{fmtEur(a.revenue_visit_any)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>}

      {/* How to read this */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">How to read this</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          <p>
            Only {fmtPct(pctWithUser)} of visits carry an identifier ({fmtN(ch.totals.visits_with_user)} of {fmtN(ch.totals.visits)}).
          </p>
          <p>
            {fmtPct(ch.revenue_without_language_pct)} of revenue falls outside the language breakdown.
          </p>
          <p>
            {fmtPct(ch.whole_business_pct_without_store)} of revenue arrives without URL and has no store attribution.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── ComparativaTab (p_cmp) ────────────────────────────────────────────────────

export function ComparativaTab({ data }: { data: ImpactDashboardData }) {
  const cmp = data.comparison;
  const dailyChartData = cmp.daily.dates.map((d, i) => ({
    date: d,
    ratio_pct: cmp.daily.ratio_pct[i] ?? null,
    ratio_7d_pct: cmp.daily.ratio_7d_pct[i] ?? null,
  }));

  const monthlyVisChartData = cmp.monthly.months.map((m, i) => ({
    month: m,
    visits_ratio_pct: cmp.monthly.visits_ratio_pct[i] ?? null,
  }));
  const monthlyRevChartData = cmp.monthly.months.map((m, i) => ({
    month: m,
    revenue_ratio_pct: cmp.monthly.revenue_ratio_pct[i] ?? null,
  }));

  const llm = cmp.totals.llm;
  const seo = cmp.totals.seo;

  return (
    <div className="space-y-4">
      {/* Daily share chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Assistant visits as % of organic, day by day</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dailyChartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={v => `${fmtN(v)} %`} />
              <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={(v: number, name: string) => [`${fmtN(v)} %`, name === 'ratio_7d_pct' ? '7d avg' : 'daily ratio']} labelFormatter={dayLabel} />
              <Line dataKey="ratio_pct" stroke="#3b82f6" dot={false} strokeWidth={1} name="ratio_pct" />
              <Line dataKey="ratio_7d_pct" stroke="#374151" dot={false} strokeWidth={1.5} strokeDasharray="4 2" name="ratio_7d_pct" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly bar charts */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly share of the assistant channel</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={monthlyVisChartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={v => `${v} %`} />
                <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={(v: number) => [`${fmtN(v)} %`, 'AI / organic visits']} labelFormatter={monthLabel} />
                <Bar dataKey="visits_ratio_pct" fill="#3b82f6" name="AI / organic visits" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly share of traced revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={monthlyRevChartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={v => `${v} %`} />
                <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={(v: number) => [`${fmtN(v)} %`, 'AI / organic revenue']} labelFormatter={monthLabel} />
                <Bar dataKey="revenue_ratio_pct" fill="#10b981" name="AI / organic revenue" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Partial-month note for comparison monthly charts */}
      {(() => {
        const lastIdx = cmp.monthly.months.length - 1;
        const lastDays = cmp.monthly.days?.[lastIdx];
        if (typeof lastDays === 'number' && lastDays < calendarDaysInMonth(cmp.monthly.months[lastIdx])) {
          return (
            <p className="text-xs text-muted-foreground">
              {monthNameCap(cmp.monthly.months[lastIdx])} has {lastDays} days so far: indicative ratios.
            </p>
          );
        }
        return null;
      })()}

      {/* Monthly table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">The table, month by month</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">AI / organic visits</TableHead>
                <TableHead className="text-right">AI / organic revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cmp.monthly.months.map((m, i) => (
                <TableRow key={m}>
                  <TableCell>{m}</TableCell>
                  <TableCell className="text-right">{fmtPct(cmp.monthly.visits_ratio_pct[i])}</TableCell>
                  <TableCell className="text-right">{fmtPct(cmp.monthly.revenue_ratio_pct[i])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Economía del canal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Channel economics, full period</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">AI assistants</TableHead>
                <TableHead className="text-right">Organic search</TableHead>
                <TableHead className="text-right">Ratio</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Products in cohort</TableCell>
                <TableCell className="text-right">{fmtN(llm.cohort_products)}</TableCell>
                <TableCell className="text-right">{fmtN(seo.cohort_products)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>Visits</TableCell>
                <TableCell className="text-right">{fmtN(llm.visits)}</TableCell>
                <TableCell className="text-right">{fmtN(seo.visits)}</TableCell>
                <TableCell className="text-right">{fmtPctSign(cmp.ratios.visits_pct)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Traced revenue</TableCell>
                <TableCell className="text-right">{fmtEur(llm.revenue_visit_any)}</TableCell>
                <TableCell className="text-right">{fmtEur(seo.revenue_visit_any)}</TableCell>
                <TableCell className="text-right">{fmtPctSign(cmp.ratios.revenue_pct)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>€ per line</TableCell>
                <TableCell className="text-right">{fmtEur(llm.eur_per_line)}</TableCell>
                <TableCell className="text-right">{fmtEur(seo.eur_per_line)}</TableCell>
                <TableCell className="text-right">{fmtPctSign(cmp.ratios.eur_per_line_diff_pct)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Products with visits</TableCell>
                <TableCell className="text-right">{fmtN(llm.products_with_visits)}</TableCell>
                <TableCell className="text-right">{fmtN(seo.products_with_visits)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>% products with visits</TableCell>
                <TableCell className="text-right">{fmtPct(llm.pct_products_with_visits)}</TableCell>
                <TableCell className="text-right">{fmtPct(seo.pct_products_with_visits)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>Visits with user</TableCell>
                <TableCell className="text-right">{fmtN(llm.visits_with_user)}</TableCell>
                <TableCell className="text-right">{fmtN(seo.visits_with_user)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>% visits with user</TableCell>
                <TableCell className="text-right">{fmtPct(llm.pct_visits_with_user)}</TableCell>
                <TableCell className="text-right">{fmtPct(seo.pct_visits_with_user)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>Lines / 100 id. visits</TableCell>
                <TableCell className="text-right">{fmtN(llm.lines_per_100_identified_visits)}</TableCell>
                <TableCell className="text-right">{fmtN(seo.lines_per_100_identified_visits)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>Conversions</TableCell>
                <TableCell className="text-right">{fmtN(llm.conv_visit_any)}</TableCell>
                <TableCell className="text-right">{fmtN(seo.conv_visit_any)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── IdiomasTab (p_idi) ────────────────────────────────────────────────────────

export function IdiomasTab({ data }: { data: ImpactDashboardData }) {
  const renderLangTable = (chKey: 'llm' | 'seo', title: string) => {
    const lang = data.languages[chKey];
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Language</TableHead>
                  <TableHead className="text-right">Visits</TableHead>
                  <TableHead className="text-right">% visits</TableHead>
                  <TableHead className="text-right">Visit revenue</TableHead>
                  <TableHead className="text-right">Purchase revenue</TableHead>
                  <TableHead className="text-right">Difference</TableHead>
                  <TableHead className="text-right">Same product</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lang.rows.map(row => (
                  <TableRow key={row.language ?? '__null__'}>
                    <TableCell>{row.language ?? '(no language)'}</TableCell>
                    <TableCell className="text-right">{fmtN(row.visits)}</TableCell>
                    <TableCell className="text-right">{fmtPct(row.pct_visits)}</TableCell>
                    <TableCell className="text-right">{fmtEur(row.revenue_visit_language)}</TableCell>
                    <TableCell className="text-right">{fmtEur(row.revenue_purchase_language)}</TableCell>
                    <TableCell className="text-right">{fmtEur(row.difference)}</TableCell>
                    <TableCell className="text-right">{fmtEur(row.revenue_same_product)}</TableCell>
                  </TableRow>
                ))}
                {/* Compras sin idioma */}
                {lang.purchases_without_language && (
                  <TableRow className="text-muted-foreground">
                    <TableCell colSpan={4}>No language</TableCell>
                    <TableCell className="text-right">{fmtEur(lang.purchases_without_language.revenue_purchase_language)}</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
                {/* Total */}
                <TableRow className="font-semibold border-t-2">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">{fmtN(lang.total.visits)}</TableCell>
                  {/* pct_visits total: 100 % by definition — sum of all language shares */}
                  <TableCell className="text-right">100 %</TableCell>
                  <TableCell className="text-right">{fmtEur(lang.total.revenue_visit_language)}</TableCell>
                  <TableCell className="text-right">{fmtEur(lang.total.revenue_purchase_language)}</TableCell>
                  <TableCell className="text-right">{fmtEur(lang.total.difference)}</TableCell>
                  <TableCell className="text-right">{fmtEur(lang.total.revenue_same_product)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            Revenue for the same product per visit: from {fmtEur(lang.worst.eur_per_visit)} in {lang.worst.language}{' '}
            to {fmtEur(lang.best.eur_per_visit)} in {lang.best.language}, a factor of {fmtN(lang.best_over_worst)}.
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {renderLangTable('llm', 'AI assistants · by language')}
      {renderLangTable('seo', 'Organic search · by language')}
    </div>
  );
}

// ── TecnicaTab (p_tec) ────────────────────────────────────────────────────────

export function TecnicaTab({ data }: { data: ImpactDashboardData }) {
  const tech = data.technical;
  const proj = tech.projection;

  return (
    <div className="space-y-4">
      {/* Where the data comes from */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Where the data comes from</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>In Analytics</TableHead>
                <TableHead>Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>{tech.bigquery_source.project}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Property</TableCell>
                <TableCell>{tech.bigquery_source.property}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Dataset</TableCell>
                <TableCell>{tech.bigquery_source.dataset}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">
            Table: <code>{tech.bigquery_source.table}</code> (format events_intraday_YYYYMMDD)
          </p>
          <p className="text-xs text-muted-foreground">
            Cohort: {fmtN(data.cohort_products)} products · snapshot {data.cohort_snapshot} · through {data.cohort_until}
          </p>
        </CardContent>
      </Card>

      {/* Data pipeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Data pipeline</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-1">
          <p>AEO cohort: {fmtN(tech.rows.cohort)} rows</p>
          <p>LLM views: {fmtN(tech.rows.llm_views)}</p>
          <p>Organic views: {fmtN(tech.rows.organic_views)}</p>
          <p>Purchases: {fmtN(tech.rows.purchases)}</p>
          <p>Total store views: {fmtN(tech.rows.store_total_views)}</p>
          <p>
            Global figure: {fmtN(tech.store_total_views.views)} total views,{' '}
            {fmtN(tech.store_total_views.views_without_store)} without store.
          </p>
        </CardContent>
      </Card>

      {/* SQL queries */}
      {tech.queries.map(q => (
        <Card key={q.num ?? q.title}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{pipelineLabel(QUERY_TITLE_LABELS, q.title, 'query-title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap">
              <code>{q.sql}</code>
            </pre>
          </CardContent>
        </Card>
      ))}

      {/* Proyección */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">How the projection is computed</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            With {fmtPct(proj.pct_no_id)} unidentified, the factor is {esESCur.format(proj.factor)}, not 1.23.{' '}
            Visible fraction: {fmtPct(proj.visible_fraction * 100)}.{' '}
            Linkable: {fmtPct(proj.pct_linkable)}.{' '}
            With user_id field: {fmtPct(proj.pct_with_user_id_field)}.{' '}
            Without user_id: {fmtPct(proj.pct_without_user_id)}.{' '}
            With NOCID: {fmtPct(proj.pct_filler_id)}.{' '}
            Correlation: {esESCur.format(proj.correlation_identified_vs_share)}.
          </p>

          {/* By month table */}
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">% unidentified</TableHead>
                <TableHead className="text-right">Visible fraction</TableHead>
                <TableHead className="text-right">Factor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.channels.llm.attribution.rows.map((row, i) => {
                const pm = proj.by_month[i];
                return (
                  <TableRow key={row.month}>
                    <TableCell>{row.month}</TableCell>
                    <TableCell className="text-right">{fmtPct(row.pct_no_id)}</TableCell>
                    <TableCell className="text-right">{pm ? fmtPct(pm.pct_visible) : '—'}</TableCell>
                    <TableCell className="text-right">{pm ? esESCur.format(pm.factor) : '—'}</TableCell>
                  </TableRow>
                );
              })}
              {/* period summary row */}
              <TableRow className="font-semibold border-t-2">
                <TableCell>period</TableCell>
                <TableCell className="text-right">{fmtPct(proj.pct_no_id)}</TableCell>
                <TableCell className="text-right">{fmtPct(proj.visible_fraction * 100)}</TableCell>
                <TableCell className="text-right">{esESCur.format(proj.factor)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* By store table */}
          {proj.stores.length > 0 && (
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Store</TableHead>
                  <TableHead className="text-right">Identifies</TableHead>
                  <TableHead className="text-right">SEO share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {proj.stores.map(s => (
                  <TableRow key={s.store}>
                    <TableCell>{s.store}</TableCell>
                    <TableCell className="text-right">{fmtPct(s.pct_identified)}</TableCell>
                    <TableCell className="text-right">{fmtPct(s.seo_share_pct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* EUR per line by group */}
          {proj.eur_per_line_by_group.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {proj.eur_per_line_by_group
                .map(g => `${pipelineLabel(PROJECTION_GROUP_LABELS, g.group, 'proj-group')} ${fmtEur(g.eur_per_line)}/line`)
                .join(' · ')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── InformeDiarioTab (main export) ────────────────────────────────────────────

export function InformeDiarioTab({ data }: { data: ImpactDashboardData }) {
  return (
    <div className="space-y-2">
      {/* Header badge */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground px-1">
        <span>Data through <strong>{data.data_until}</strong></span>
        <span>·</span>
        <span>Generated {new Date(data.generated_at).toLocaleString('en-US')}</span>
        <span>·</span>
        <span>Schema v{data.schema_version}</span>
      </div>

      <Tabs defaultValue="p_llm">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="p_llm">AI assistants</TabsTrigger>
          <TabsTrigger value="p_seo">Organic search</TabsTrigger>
          <TabsTrigger value="p_cmp">Comparison</TabsTrigger>
          <TabsTrigger value="p_idi">Languages</TabsTrigger>
          <TabsTrigger value="p_tec">How it's built</TabsTrigger>
        </TabsList>

        <TabsContent value="p_llm" className="mt-4">
          <ChannelTab ch={data.channels.llm} comparison={data.comparison} chKey="llm" />
        </TabsContent>

        <TabsContent value="p_seo" className="mt-4">
          <ChannelTab ch={data.channels.seo} comparison={data.comparison} chKey="seo" />
        </TabsContent>

        <TabsContent value="p_cmp" className="mt-4">
          <ComparativaTab data={data} />
        </TabsContent>

        <TabsContent value="p_idi" className="mt-4">
          <IdiomasTab data={data} />
        </TabsContent>

        <TabsContent value="p_tec" className="mt-4">
          <TecnicaTab data={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
